import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import Layout from "../components/Layout.jsx";
import { dashboardService } from "../services/apiService.jsx";
import "./MisDashboard.css";

const STATUS_OPTIONS = ["All", "Active", "Pending", "Ongoing", "Completed"];

const getTrackerData = (response) => response?.data?.data || response?.data || response || {};

const formatNumber = (value) => Number(value || 0).toLocaleString("en-IN");

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString("en-GB");
};

const normalizeStatus = (value) => {
  const status = String(value || "Pending").toLowerCase();
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const progressClass = (progress) => {
  if (progress >= 80) return "is-strong";
  if (progress >= 45) return "is-steady";
  return "is-behind";
};

const ProgressBar = ({ value }) => (
  <div className="mis-progress-cell">
    <div className="mis-progress-track" aria-label={`${value}% complete`}>
      <span className={`mis-progress-fill ${progressClass(value)}`} style={{ width: `${Math.min(Math.max(value, 0), 100)}%` }} />
    </div>
    <strong>{value}%</strong>
  </div>
);

const MisDashboard = () => {
  const { user } = useAuth();
  const isAdmin = String(user?.role || "").toLowerCase() === "admin";
  const [activeView, setActiveView] = useState("projects");
  const [statusFilter, setStatusFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [tracker, setTracker] = useState({ projects: [], employees: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }

    dashboardService.getMisPerformanceTracker()
      .then((response) => {
        const data = getTrackerData(response);
        setTracker({
          projects: Array.isArray(data.projects) ? data.projects : [],
          employees: Array.isArray(data.employees) ? data.employees : [],
          summary: data.summary || {},
        });
        setError("");
      })
      .catch((requestError) => {
        console.error("Unable to load MIS performance tracker", requestError);
        setError(requestError.response?.data?.message || "Performance tracker could not be loaded.");
      })
      .finally(() => setLoading(false));
  }, [isAdmin]);

  const filteredProjects = useMemo(() => {
    const query = search.trim().toLowerCase();
    return tracker.projects.filter((project) => {
      const matchesStatus = statusFilter === "All" || normalizeStatus(project.project_status) === statusFilter;
      const matchesSearch = !query || [project.project_name, project.customer_name, project.employee_name, project.department_name]
        .some((value) => String(value || "").toLowerCase().includes(query));
      return matchesStatus && matchesSearch;
    });
  }, [search, statusFilter, tracker.projects]);

  const filteredEmployees = useMemo(() => {
    const query = search.trim().toLowerCase();
    return tracker.employees.filter((employee) => !query || [employee.employee_name, employee.department_name]
      .some((value) => String(value || "").toLowerCase().includes(query)));
  }, [search, tracker.employees]);

  const summary = tracker.summary;
  const completionRate = summary.total_tasks ? Math.round((summary.completed_tasks / summary.total_tasks) * 100) : 0;

  if (!isAdmin) {
    return <Layout><div className="mis-empty-state"><i className="bi bi-shield-lock"></i><h2>Admin access required</h2><p>This MIS tracker is available only to administrators.</p></div></Layout>;
  }

  return (
    <Layout>
      <main className="mis-dashboard container-fluid">
        <header className="mis-header">
          <div>
            <span className="mis-eyebrow"><i className="bi bi-bar-chart-line-fill"></i> MANAGEMENT INFORMATION SYSTEM</span>
            <h1>Performance tracker</h1>
            <p>See project delivery and employee performance in one operational view.</p>
          </div>
          <button type="button" className="mis-refresh-button" onClick={() => window.location.reload()} title="Refresh tracker" aria-label="Refresh tracker">
            <i className="bi bi-arrow-clockwise"></i>
          </button>
        </header>

        {error && <div className="alert alert-danger" role="alert">{error}</div>}

        <section className="mis-summary-grid" aria-label="Performance summary">
          <div className="mis-summary-card"><span className="mis-summary-icon blue"><i className="bi bi-kanban"></i></span><div><small>Total projects</small><strong>{formatNumber(summary.projects)}</strong><span>{formatNumber(summary.completed_projects)} completed</span></div></div>
          <div className="mis-summary-card"><span className="mis-summary-icon green"><i className="bi bi-people"></i></span><div><small>Active employees</small><strong>{formatNumber(summary.employees)}</strong><span>Across all departments</span></div></div>
          <div className="mis-summary-card"><span className="mis-summary-icon amber"><i className="bi bi-check2-square"></i></span><div><small>Task completion</small><strong>{completionRate}%</strong><span>{formatNumber(summary.completed_tasks)} of {formatNumber(summary.total_tasks)} tasks</span></div></div>
        </section>

        <section className="mis-tracker-panel">
          <div className="mis-panel-header">
            <div>
              <span className="mis-section-kicker">LIVE OPERATIONS</span>
              <h2>{activeView === "projects" ? "Project performance" : "Employee performance"}</h2>
            </div>
            <div className="mis-view-tabs" role="tablist" aria-label="Tracker view">
              <button type="button" className={activeView === "projects" ? "active" : ""} onClick={() => setActiveView("projects")}><i className="bi bi-kanban"></i> Projects</button>
              <button type="button" className={activeView === "employees" ? "active" : ""} onClick={() => setActiveView("employees")}><i className="bi bi-person-lines-fill"></i> Employees</button>
            </div>
          </div>

          <div className="mis-filter-row">
            <label className="mis-search"><i className="bi bi-search"></i><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={activeView === "projects" ? "Search project, client or owner" : "Search employee or department"} /></label>
            {activeView === "projects" && <div className="mis-status-tabs" role="tablist" aria-label="Project status">{STATUS_OPTIONS.map((status) => <button type="button" key={status} className={statusFilter === status ? "active" : ""} onClick={() => setStatusFilter(status)}>{status}</button>)}</div>}
          </div>

          {loading ? <div className="mis-loading"><span className="spinner-border spinner-border-sm"></span> Loading performance data...</div> : activeView === "projects" ? (
            <div className="table-responsive">
              <table className="table mis-table"><thead><tr><th>Project</th><th>Owner</th><th>Status</th><th>Timeline</th><th>Hours</th><th>Tasks</th><th>Progress</th></tr></thead><tbody>
                {filteredProjects.map((project) => <tr key={project.project_id}><td><strong>{project.project_name || "Unnamed project"}</strong><small>{project.customer_name || "No client"}</small></td><td>{project.employee_name || "Unassigned"}<small>{project.department_name || "-"}</small></td><td><span className={`mis-status status-${String(project.project_status || "pending").toLowerCase()}`}>{normalizeStatus(project.project_status)}</span></td><td>{formatDate(project.project_start_date)} <span>to</span> {formatDate(project.project_end_date)}</td><td>{formatNumber(project.logged_hours)} / {formatNumber(project.allocated_hours)}h</td><td>{formatNumber(project.task_completed)} / {formatNumber(project.task_total)}</td><td><ProgressBar value={project.progress} /></td></tr>)}
              </tbody></table>
              {!filteredProjects.length && <div className="mis-no-results">No projects match the selected filters.</div>}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table mis-table"><thead><tr><th>Employee</th><th>Department</th><th>Projects</th><th>Hours</th><th>Tasks completed</th><th>Performance</th></tr></thead><tbody>
                {filteredEmployees.map((employee) => <tr key={employee.employee_id}><td><strong>{employee.employee_name || "Unnamed employee"}</strong><small>Employee #{employee.employee_id}</small></td><td>{employee.department_name || "-"}</td><td>{formatNumber(employee.project_completed)} / {formatNumber(employee.project_total)}</td><td>{formatNumber(employee.logged_hours)} / {formatNumber(employee.allocated_hours)}h</td><td>{formatNumber(employee.task_completed)} / {formatNumber(employee.task_total)}</td><td><ProgressBar value={employee.progress} /></td></tr>)}
              </tbody></table>
              {!filteredEmployees.length && <div className="mis-no-results">No employees match the search.</div>}
            </div>
          )}
        </section>
      </main>
    </Layout>
  );
};

export default MisDashboard;
